import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_LABEL = { 1: "Ad hoc", 2: "Repeatable", 3: "Defined", 4: "Managed", 5: "Optimising" };
const LEVEL_DOT = { 1: "bg-rose-500", 2: "bg-orange-400", 3: "bg-amber-400", 4: "bg-emerald-400", 5: "bg-emerald-600" };
const ROLES = ["CRO", "CISO", "PMO Director", "Transformation Director", "CFO", "CEO", "Consultant / Advisor", "Other"];
const INDUSTRIES = [
  "Financial Services", "Manufacturing", "Energy & Utilities",
  "Healthcare & Life Sciences", "Public Sector", "Retail & Consumer",
  "Technology & SaaS", "Aerospace & Defence", "Professional Services",
  "Logistics & Supply Chain", "Other",
];

export default function MaturityAssessment() {
  const [rubric, setRubric] = useState(null);
  const [answers, setAnswers] = useState({});
  const [organisation, setOrganisation] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [assessmentId, setAssessmentId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/maturity-assessment/rubric`).then(({ data }) => setRubric(data)).catch(() => setError("Could not load assessment"));
  }, []);

  const totalIndicators = useMemo(() => rubric ? rubric.dimensions.reduce((n, d) => n + d.indicators.length, 0) : 0, [rubric]);
  const answeredCount = useMemo(() => Object.values(answers).filter((v) => v).length, [answers]);

  const setAns = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/maturity-assessment/submit`, { answers, organisation: organisation || null, role: role || null, email: email || null, industry: industry || null });
      setScorecard(data.scorecard);
      setAssessmentId(data.assessment_id || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (!rubric) return <div className="min-h-screen bg-slate-50 grid place-items-center text-slate-500">Loading EKSELENS PMO360 rubric…</div>;

  if (scorecard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-violet-100" data-testid="maturity-scorecard">
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">EKSELENS PMO360 · Maturity assessment result</p>
            <div className="mt-3 flex items-baseline gap-4">
              <h1 className="text-5xl font-bold tracking-tight text-violet-900" data-testid="overall-level">Level {scorecard.overall_level}</h1>
              <span className="text-2xl font-semibold text-slate-700">{scorecard.overall_level_label}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">Composite score <strong>{scorecard.overall_score.toFixed(2)}</strong> · Confidence <strong>{scorecard.confidence}</strong> · {scorecard.answered_count}/{scorecard.indicator_count} indicators answered</p>
            {scorecard.overall_capped_by_dealbreaker && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="dealbreaker-notice">
                <strong>Capped at Level 2.</strong> One or more dimensions fell below the EKSELENS dealbreaker threshold ({scorecard.dealbreakers.map((d) => d.dimension_name).join(", ")}). Overall maturity is capped until these are addressed.
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {scorecard.dimensions.map((d) => (
              <div key={d.dimension_id} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">{d.dimension_name}</h3>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white ${LEVEL_DOT[d.level]}`}>L{d.level} · {d.level_label}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">Score {d.score.toFixed(2)} · weight {d.weight}</p>
              </div>
            ))}
          </div>

          {scorecard.top_gaps?.length > 0 && (
            <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100" data-testid="top-gaps">
              <h3 className="text-lg font-semibold text-violet-900">Top {scorecard.top_gaps.length} evidence gaps</h3>
              <p className="text-xs text-slate-500 mb-4">Lowest-scoring indicators in your highest-weighted dimensions — anchored to the EKSELENS PMO360 rubric.</p>
              <ul className="space-y-3">
                {scorecard.top_gaps.map((g) => (
                  <li key={g.indicator_id} className="rounded-lg border-l-4 border-violet-400 bg-violet-50/40 px-4 py-3">
                    <p className="font-medium text-slate-800">{g.dimension_name} · {g.indicator_name} <span className="text-xs text-slate-500">(Level {g.score})</span></p>
                    <p className="mt-1 text-sm text-slate-600 italic">&ldquo;{g.gap_text}&rdquo;</p>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <strong>Tools available to close these gaps:</strong> EKSELENS <strong>PMO360</strong> (process discipline), <strong>BENEFITS360</strong> (value tracking), and ERIO (live risk intelligence layer). The ERIO pilot includes a deeper Plan Integrity Score and Risk Register Quality Score that target these specific weaknesses.
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {assessmentId && (
              <a
                href={`${API}/maturity-assessment/${assessmentId}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-violet-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-800"
                data-testid="download-radar-pdf-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download radar PDF
              </a>
            )}
            <a href="/ERIO-position-paper.pdf" target="_blank" rel="noreferrer" className="rounded-lg border border-violet-300 bg-white px-5 py-2.5 text-sm font-medium text-violet-800 hover:bg-violet-50" data-testid="cta-paper">Read the full position paper</a>
            <button onClick={() => { setScorecard(null); setAssessmentId(""); setAnswers({}); }} className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="reset-btn">Run another assessment</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">EKSELENS · ERIO · Lead-magnet</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-violet-950">PPM Maturity &amp; Portfolio-Fit Assessment</h1>
        <p className="mt-4 text-base text-slate-700 max-w-2xl">Where does your project portfolio actually sit on the EKSELENS PMO360 maturity curve? Answer 24 indicators across 6 dimensions to see your level, your top evidence gaps, and where ERIO can help.</p>

        <div className="mt-6 rounded-xl bg-white p-4 ring-1 ring-slate-100 text-sm text-slate-600 flex items-center gap-4">
          <span><strong>{answeredCount}</strong> / {totalIndicators} indicators answered</span>
          <div className="flex-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-violet-500 transition-all" style={{ width: `${(answeredCount/totalIndicators)*100}%` }}></div></div>
        </div>

        {rubric.dimensions.map((d) => (
          <section key={d.dimension_id} className="mt-8" data-testid={`dimension-${d.dimension_id}`}>
            <h2 className="text-xl font-semibold text-violet-900">{d.dimension_name}</h2>
            <p className="text-xs text-slate-500 mb-3">Weight {d.weight}</p>
            <div className="space-y-5">
              {d.indicators.map((i) => (
                <div key={i.indicator_id} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100" data-testid={`indicator-${i.indicator_id}`}>
                  <p className="font-medium text-slate-800">{i.indicator_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Pick the level that best describes your current state.</p>
                  <div className="mt-3 grid gap-2">
                    {[1,2,3,4,5].map((lvl) => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => setAns(i.indicator_id, lvl)}
                        data-testid={`answer-${i.indicator_id}-l${lvl}`}
                        className={`text-left rounded-lg border px-3 py-2.5 text-sm transition ${
                          answers[i.indicator_id] === lvl
                            ? "border-violet-500 bg-violet-50 ring-2 ring-violet-200"
                            : "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/40"
                        }`}
                      >
                        <span className={`inline-block w-6 text-xs font-semibold ${answers[i.indicator_id] === lvl ? "text-violet-700" : "text-slate-400"}`}>L{lvl}</span>
                        <span className="text-slate-700">{i.level_descriptors[lvl]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
          <h3 className="font-semibold text-violet-900">Optional — for a personalised follow-up</h3>
          <p className="text-xs text-slate-500 mb-4">If you&rsquo;d like the role-tuned PDF report, leave your details. We won&rsquo;t contact you otherwise.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Organisation" className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="org-input" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="role-select">
              <option value="">Your role…</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="industry-select">
              <option value="">Your industry… (optional)</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" type="email" className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="email-input" />
          </div>
        </div>

        {error && <p className="mt-4 rounded-lg bg-rose-50 px-4 py-2 text-sm text-rose-700" data-testid="error-msg">{error}</p>}

        <div className="mt-6 flex items-center gap-3">
          <button onClick={submit} disabled={submitting} className="rounded-lg bg-violet-700 px-6 py-3 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50" data-testid="submit-btn">
            {submitting ? "Scoring…" : "See my maturity level"}
          </button>
          <p className="text-xs text-slate-500">You can submit with fewer than 24 answers — confidence will be marked LOW.</p>
        </div>

        <p className="mt-12 text-xs text-slate-400">Powered by the EKSELENS PMO360 rubric · Defensible AI Charter applies · No login required</p>
      </div>
    </div>
  );
}
